import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/store/pinia/store.js'
import { UserRole, isLandlordRole, isTenantRole, isEmployeeRole, isAdminRole, getUserDashboardRoute } from '@/enums/roles.js'

// ─── 1. АВТОРИЗАЦИЯ И РЕГИСТРАЦИЯ ─────────────────────────────────────
const authChildRoutes = (prefix) => [
  {
    path: 'login',
    name: prefix + '.login',
    alias: '/login',
    meta: { auth: false, guestOnly: true, name: 'Вход' },
    component: () => import('@/views/auth/default/SignIn.vue')
  },
  {
    path: 'register',
    name: prefix + '.register',
    alias: '/signup',
    meta: { auth: false, guestOnly: true, name: 'Регистрация' },
    component: () => import('@/views/auth/default/SignUp.vue')
  },
  {
    path: 'reset-password',
    name: prefix + '.reset-password',
    meta: { auth: false, guestOnly: true, name: 'Восстановление пароля' },
    component: () => import('@/views/auth/default/ResetPassword.vue')
  },
  {
    path: 'varify-email',
    name: prefix + '.varify-email',
    meta: { auth: false, guestOnly: false, name: 'Подтверждение Email' },
    component: () => import('@/views/auth/default/VarifyEmail.vue')
  },
  {
    path: 'lock-screen',
    name: prefix + '.lock-screen',
    meta: { auth: false, guestOnly: true, name: 'Блокировка' },
    component: () => import('@/views/auth/default/LockScreen.vue')
  },
  {
    path: 'welcome',
    name: prefix + '.welcome',
    alias: '/welcome',
    meta: { auth: true, guestOnly: false, noRoleOnly: true, name: 'Выбор роли' },
    component: () => import('@/views/role/welcome.vue')
  }
]

// ─── 2. МАРШРУТЫ ОШИБОК ───────────────────────────────────────────────
const errorRoutes = (prefix) => [
  {
    path: '404',
    name: prefix + '.404',
    meta: { auth: false, name: 'Страница не найдена' },
    component: () => import('@/views/errors/Error404Page.vue')
  },
  {
    path: '500',
    name: prefix + '.500',
    meta: { auth: false, name: 'Ошибка сервера' },
    component: () => import('@/views/errors/Error500Page.vue')
  },
  {
    path: 'maintenance',
    name: prefix + '.maintenance',
    meta: { auth: false, name: 'Технические работы' },
    component: () => import('@/views/errors/MaintenancePage.vue')
  }
]

// ─── 3. МАРШРУТЫ АРЕНДОДАТЕЛЯ (role_id: 1) ───────────────────────────
const landlordChildRoutes = (prefix) => [
  {
    path: '',
    name: prefix + '.dashboard',
    meta: { auth: true, roles: [UserRole.LANDLORD, UserRole.ALL_IN_ONE], name: 'Дашборд Арендодателя' },
    component: () => import('@/views/landlord/DashboardPage.vue')
  },
  {
    path: 'objects',
    name: prefix + '.objects',
    meta: { auth: true, roles: [UserRole.LANDLORD, UserRole.ALL_IN_ONE], name: 'Объекты недвижимости' },
    component: () => import('@/views/landlord/ObjectsPage.vue')
  },
  {
    path: 'tenants',
    name: prefix + '.tenants',
    meta: { auth: true, roles: [UserRole.LANDLORD, UserRole.ALL_IN_ONE], name: 'Арендаторы' },
    component: () => import('@/views/landlord/TenantsPage.vue')
  },
  {
    path: 'utilities',
    name: prefix + '.utilities',
    meta: { auth: true, roles: [UserRole.LANDLORD, UserRole.ALL_IN_ONE], name: 'ЖКУ и счётчики' },
    component: () => import('@/views/landlord/UtilitiesPage.vue')
  },
  {
    path: 'rent-payments',
    name: prefix + '.rent-payments',
    meta: { auth: true, roles: [UserRole.LANDLORD, UserRole.ALL_IN_ONE], name: 'Платежи аренды' },
    component: () => import('@/views/landlord/RentPaymentsPage.vue')
  },
  {
    path: 'tickets',
    name: prefix + '.tickets',
    meta: { auth: true, roles: [UserRole.LANDLORD, UserRole.ALL_IN_ONE], name: 'Обращения' },
    component: () => import('@/views/landlord/TicketsPage.vue')
  },
  {
    path: 'settings',
    name: prefix + '.settings',
    meta: { auth: true, roles: [UserRole.LANDLORD, UserRole.ALL_IN_ONE], name: 'Настройки' },
    component: () => import('@/views/settings/SettingsPage.vue')
  },
  {
    path: 'support',
    name: prefix + '.support',
    meta: { auth: true, roles: [UserRole.LANDLORD, UserRole.ALL_IN_ONE], name: 'Служба поддержки' },
    component: () => import('@/views/support/SupportPage.vue')
  }
]

// ─── 4. МАРШРУТЫ СЪЕМЩИКА / АРЕНДАТОРА (role_id: 2) ──────────────────
const tenantChildRoutes = (prefix) => [
  {
    path: '',
    name: prefix + '.dashboard',
    meta: { auth: true, roles: [UserRole.TENANT, UserRole.ALL_IN_ONE], name: 'Кабинет Арендатора' },
    component: () => import('@/views/tenant/DashboardPage.vue')
  },
  {
    path: 'objects',
    name: prefix + '.objects',
    meta: { auth: true, roles: [UserRole.TENANT, UserRole.ALL_IN_ONE], name: 'Мои объекты аренды' },
    component: () => import('@/views/tenant/ObjectsPage.vue')
  },
  {
    path: 'landlords',
    name: prefix + '.landlords',
    meta: { auth: true, roles: [UserRole.TENANT, UserRole.ALL_IN_ONE], name: 'Арендодатели' },
    component: () => import('@/views/tenant/TenantsPage.vue')
  },
  {
    path: 'employees',
    name: prefix + '.employees',
    meta: { auth: true, roles: [UserRole.TENANT, UserRole.ALL_IN_ONE], name: 'Сотрудники' },
    component: () => import('@/views/tenant/EmployeesPage.vue')
  },
  {
    path: 'finance',
    name: prefix + '.finance',
    meta: { auth: true, roles: [UserRole.TENANT, UserRole.ALL_IN_ONE], name: 'Финансы' },
    component: () => import('@/views/tenant/FinancePage.vue')
  },
  {
    path: 'tickets',
    name: prefix + '.tickets',
    meta: { auth: true, roles: [UserRole.TENANT, UserRole.ALL_IN_ONE], name: 'Обращения' },
    component: () => import('@/views/tenant/TicketsPage.vue')
  },
  {
    path: 'settings',
    name: prefix + '.settings',
    meta: { auth: true, roles: [UserRole.TENANT, UserRole.ALL_IN_ONE], name: 'Настройки' },
    component: () => import('@/views/settings/SettingsPage.vue')
  },
  {
    path: 'support',
    name: prefix + '.support',
    meta: { auth: true, roles: [UserRole.TENANT, UserRole.ALL_IN_ONE], name: 'Служба поддержки' },
    component: () => import('@/views/support/SupportPage.vue')
  }
]

// ─── 5. МАРШРУТЫ СОТРУДНИКА АРЕНДАТОРА (role_id: 4) ───────────────────
const employerChildRoutes = (prefix) => [
  {
    path: '',
    name: prefix + '.dashboard',
    meta: { auth: true, roles: [UserRole.EMPLOYEE], name: 'Дашборд Сотрудника' },
    component: () => import('@/views/employer/DashboardPage.vue')
  },
  {
    path: 'shifts',
    name: prefix + '.shifts',
    meta: { auth: true, roles: [UserRole.EMPLOYEE], name: 'Смены и статистика' },
    component: () => import('@/views/employer/ShiftsPage.vue')
  },
  {
    path: 'chat',
    name: prefix + '.chat',
    meta: { auth: true, roles: [UserRole.EMPLOYEE], name: 'Чат' },
    component: () => import('@/views/employer/ChatPage.vue')
  },
  {
    path: 'support',
    name: prefix + '.support',
    meta: { auth: true, roles: [UserRole.EMPLOYEE], name: 'Служба поддержки' },
    component: () => import('@/views/support/SupportPage.vue')
  }
]

const adminChildRoutes = (prefix) => [
  {
    path: '',
    name: prefix + '.dashboard',
    meta: { auth: true, roles: [UserRole.ADMIN], name: 'Дашборд Администратора' },
    component: () => import('@/views/admin/Dashboard.vue')
  },
  {
    path: 'blog',
    name: prefix + '.blog',
    meta: { auth: true, roles: [UserRole.ADMIN], name: 'Управление блогом' },
    component: () => import('@/views/admin/blog/AdminBlogListPage.vue')
  },
  {
    path: 'blog/create',
    name: prefix + '.blog.create',
    meta: { auth: true, roles: [UserRole.ADMIN], name: 'Новая публикация' },
    component: () => import('@/views/admin/blog/AdminBlogEditorPage.vue')
  },
  {
    path: 'blog/edit/:id',
    name: prefix + '.blog.edit',
    meta: { auth: true, roles: [UserRole.ADMIN], name: 'Редактирование публикации' },
    component: () => import('@/views/admin/blog/AdminBlogEditorPage.vue')
  },
  {
    path: 'users',
    name: prefix + '.users',
    meta: { auth: true, roles: [UserRole.ADMIN], name: 'Пользователи системы' },
    component: () => import('@/views/admin/users/AdminUsersPage.vue')
  },
  {
    path: 'objects',
    name: prefix + '.objects',
    meta: { auth: true, roles: [UserRole.ADMIN], name: 'Объекты платформы' },
    component: () => import('@/views/admin/objects/AdminObjectsPage.vue')
  },
  {
    path: 'system',
    name: prefix + '.system',
    meta: { auth: true, roles: [UserRole.ADMIN], name: 'Система и логи' },
    component: () => import('@/views/admin/system/AdminSystemPage.vue')
  }
]

// ─── 0. ПУБЛИЧНЫЕ СТРАНИЦЫ (ЛЕНДИНГ, БЛОГ, FAQ, О НАС) ────────────────
const publicChildRoutes = (prefix) => [
  {
    path: '',
    name: prefix + '.landing',
    meta: { auth: false, name: 'Главная — Мой Арендатор' },
    component: () => import('@/views/public/LandingPage.vue')
  },
  {
    path: 'blog',
    name: prefix + '.blog',
    meta: { auth: false, name: 'Блог и новости' },
    component: () => import('@/views/public/BlogPage.vue')
  },
  {
    path: 'blog/:slug',
    name: prefix + '.blog.post',
    meta: { auth: false, name: 'Публикация' },
    component: () => import('@/views/public/BlogPostPage.vue')
  },
  {
    path: 'faq',
    name: prefix + '.faq',
    meta: { auth: false, name: 'Частые вопросы (FAQ)' },
    component: () => import('@/views/public/FaqPage.vue')
  },
  {
    path: 'about',
    name: prefix + '.about',
    meta: { auth: false, name: 'О сервисе' },
    component: () => import('@/views/public/AboutPage.vue')
  },
  {
    path: 'terms',
    name: prefix + '.terms',
    meta: { auth: false, name: 'Пользовательское соглашение' },
    component: () => import('@/views/public/TermsPage.vue')
  },
  {
    path: 'privacy',
    name: prefix + '.privacy',
    meta: { auth: false, name: 'Политика конфиденциальности' },
    component: () => import('@/views/public/PrivacyPage.vue')
  },
  {
    path: 'rules',
    name: prefix + '.rules',
    meta: { auth: false, name: 'Условия использования' },
    component: () => import('@/views/public/TermsOfUsePage.vue')
  },
  {
    path: 'invite/landlord/:token',
    name: prefix + '.invite.landlord',
    meta: { auth: false, name: 'Приглашение собственника' },
    component: () => import('@/views/public/InviteLandlordPage.vue')
  }
]

// ─── КОРНЕВЫЕ МАРШРУТЫ ПРИЛОЖЕНИЯ ─────────────────────────────────────
const routes = [
  // Публичные страницы (Лендинг, Блог, FAQ, О сервисе, Соглашения)
  {
    path: '/',
    component: () => import('../layouts/public/PublicLayout.vue'),
    meta: { auth: false },
    children: publicChildRoutes('public')
  },

  // Авторизация
  {
    path: '/auth',
    name: 'auth',
    component: () => import('../layouts/guest/BlankLayout.vue'),
    children: authChildRoutes('auth')
  },

  // Кабинет Арендодателя
  {
    path: '/landlord',
    name: 'landlord',
    meta: { auth: true, roles: [UserRole.LANDLORD, UserRole.ALL_IN_ONE] },
    component: () => import('../layouts/DefaultLayout.vue'),
    children: landlordChildRoutes('landlord')
  },

  // Кабинет Арендатора
  {
    path: '/tenant',
    name: 'tenant',
    meta: { auth: true, roles: [UserRole.TENANT, UserRole.ALL_IN_ONE] },
    component: () => import('../layouts/DefaultLayout.vue'),
    children: tenantChildRoutes('tenant')
  },

  // Кабинет Сотрудника (Employer/Employee)
  {
    path: '/employer',
    name: 'employer',
    meta: { auth: true, roles: [UserRole.EMPLOYEE] },
    component: () => import('../layouts/DefaultLayout.vue'),
    children: employerChildRoutes('employer')
  },

  //Кабинет администратора
  {
    path: '/admin',
    name: 'admin',
    meta: { auth: true, roles: [UserRole.ADMIN] },
    component: () => import('../layouts/admin/AdminLayout.vue'),
    children: adminChildRoutes('admin')
  },

  // Быстрый переход в поддержку для любого авторизованного пользователя
  {
    path: '/support',
    name: 'support.root',
    meta: { auth: true },
    redirect: () => {
      const authStore = useAuthStore()
      if (isTenantRole(authStore.user?.role_id) && Number(authStore.user?.role_id) === UserRole.TENANT) {
        return { name: 'tenant.support' }
      }
      if (isEmployeeRole(authStore.user?.role_id)) {
        return { name: 'employer.support' }
      }
      return { name: 'landlord.support' }
    }
  },

  // Ошибки
  {
    path: '/errors',
    name: 'errors',
    component: () => import('../layouts/guest/BlankLayout.vue'),
    children: errorRoutes('errors')
  },

  // Обработка неизвестных путей (404)
  {
    path: '/:pathMatch(.*)*',
    redirect: '/errors/404'
  }
]

const router = createRouter({
  linkActiveClass: 'active',
  linkExactActiveClass: 'exact-active',
  history: createWebHistory(process.env.BASE_URL),
  base: process.env.BASE_URL,
  routes
})

// ─── NAVIGATION GUARD ────────────────────────────────────────────────
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  // 0. Автоматическая синхронизация актуального профиля пользователя с бэкенда
  if (authStore.token && !authStore.isLoaded) {
    try {
      await authStore.fetchUser()
    } catch (e) {
      console.warn('Синхронизация профиля в router guard:', e)
    }
  }

  // Проверяем наличие и данных пользователя, и токена доступа
  const isAuthenticated = !!authStore.user && !!authStore.token

  // 0.1 В нативном приложении (Tauri) не показываем веб-лендинг
  const isTauri = typeof window !== 'undefined' && Boolean(window.__TAURI_INTERNALS__ || window.__TAURI__)
  if (isTauri && to.path === '/') {
    if (!isAuthenticated) {
      return next({ path: '/login' })
    }
    return next(getUserDashboardRoute(authStore.user?.role_id))
  }

  // 0.2 На вебе: авторизованный пользователь при открытии корня '/' направляется сразу в дашборд
  // (за исключением явного предварительного просмотра лендинга админом через ?preview=1 или ?landing=1)
  if (to.path === '/' && isAuthenticated && !to.query.preview && !to.query.landing) {
    return next(getUserDashboardRoute(authStore.user?.role_id))
  }

  const requiresAuth = to.matched.some(record => record.meta.auth === true)
  const isGuestOnly = to.matched.some(record => record.meta.guestOnly === true)

  // 1. Неавторизованный пользователь пытается зайти на защищённый маршрут
  if (requiresAuth && !isAuthenticated) {
    const redirectQuery = (to.fullPath && to.fullPath !== '/' && !to.fullPath.startsWith('/login') && !to.fullPath.startsWith('/signup'))
      ? { redirect: to.fullPath }
      : undefined

    return next({ path: '/login', query: redirectQuery })
  }

  // 2. Авторизованный пользователь открывает гостевой маршрут (логин/регистрация)
  if (isGuestOnly && isAuthenticated) {
    return next(getUserDashboardRoute(authStore.user?.role_id))
  }

  // 3. Авторизованный пользователь с уже выбранной ролью пытается зайти на выбор роли (/welcome)
  const isNoRoleOnly = to.matched.some(record => record.meta.noRoleOnly === true)
  if (isNoRoleOnly && isAuthenticated && authStore.user?.role_id !== null && authStore.user?.role_id !== undefined) {
    return next(getUserDashboardRoute(authStore.user.role_id))
  }

  // 4. СТРОГАЯ ИЗОЛЯЦИЯ КАБИНЕТОВ ПО РОЛЯМ:
  if (isAuthenticated && authStore.user?.role_id !== null && authStore.user?.role_id !== undefined) {
    const roleId = Number(authStore.user.role_id)

    // АДМИНИСТРАТОР (роль 99):
    // Имеет доступ ТОЛЬКО к разделу /admin и системным страницам.
    // Доступ к кабинетам /landlord, /tenant, /employer КАТЕГОРИЧЕСКИ ЗАПРЕЩЁН!
    if (isAdminRole(roleId)) {
      if (to.path.startsWith('/landlord') || to.path.startsWith('/tenant') || to.path.startsWith('/employer')) {
        return next({ name: 'admin.dashboard' })
      }
    } else {
      // НЕ-АДМИНИСТРАТОРЫ (1, 2, 3, 4):
      // Категорически ЗАПРЕЩЕНО открывать раздел /admin!
      if (to.path.startsWith('/admin')) {
        return next(getUserDashboardRoute(roleId))
      }
    }
  }

  // 5. Проверка прав доступа по массиву roles маршрута (если явно задан)
  const matchedRoles = to.matched
    .map(record => record.meta?.roles)
    .filter(Boolean)
    .flat()

  if (matchedRoles.length > 0 && isAuthenticated && authStore.user?.role_id) {
    const roleId = Number(authStore.user.role_id)
    if (!matchedRoles.includes(roleId)) {
      return next(getUserDashboardRoute(roleId))
    }
  }

  next()
})

router.afterEach((to) => {
  const pageName = to.meta?.name
  if (pageName) {
    document.title = `${pageName} — Мой Арендатор`
  } else {
    document.title = 'Мой Арендатор — Управление коммерческой недвижимостью'
  }

  // Обновление canonical URL с гарантированным протоколом https://
  if (typeof document !== 'undefined') {
    let canonical = document.querySelector('link[rel="canonical"]')
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.setAttribute('rel', 'canonical')
      document.head.appendChild(canonical)
    }
    const cleanPath = to.path === '/' ? '' : to.path
    canonical.setAttribute('href', `https://moyarendator.ru${cleanPath}`)
  }
})

export default router
